import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { platformClient } from "@homy/api-client";
import {
  PageContainer,
  PageHeader,
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Alert,
  AlertDescription,
} from "@homy/ui";
import {
  Megaphone,
  Globe2,
  MapPin,
  RefreshCw,
  Search,
  Filter,
  Eye,
  MessageSquare,
  Building2,
  TrendingUp,
  Share2,
  CheckCircle2,
  Clock,
  Layers,
  Sparkles,
  ArrowUpRight,
  Radio,
} from "lucide-react";

const COUNTRY_FLAGS = {
  CL: "🇨🇱",
  ES: "🇪🇸",
  US: "🇺🇸",
  MX: "🇲🇽",
  AR: "🇦🇷",
  CO: "🇨🇴",
  PE: "🇵🇪",
};

export function PublicationsPage() {
  const [selectedCountry, setSelectedCountry] = useState("ALL");
  const [zoneSearch, setZoneSearch] = useState("");

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["platform-publications-dashboard"],
    queryFn: async () => {
      const res = await platformClient.get("/publications/dashboard");
      return res.data;
    },
    refetchInterval: 30000,
  });

  const filteredZones = useMemo(() => {
    if (!data?.byZone) return [];
    return data.byZone.filter((item) => {
      const matchCountry =
        selectedCountry === "ALL" || item.countryCode === selectedCountry;
      const matchSearch =
        !zoneSearch ||
        item.zone.toLowerCase().includes(zoneSearch.toLowerCase()) ||
        item.city.toLowerCase().includes(zoneSearch.toLowerCase());
      return matchCountry && matchSearch;
    });
  }, [data?.byZone, selectedCountry, zoneSearch]);

  const activeRate = data?.totalListings
    ? Math.round((data.activeListings / data.totalListings) * 100)
    : 0;

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Dynamic Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border/60 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center text-primary">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-foreground">
                  Publications Dashboard
                </h1>
                <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs py-0.5 gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live Platform Feed
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Macro-level publication tracking across all tenant organizations, countries, and metropolitan zones.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs h-8"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh Data
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load platform publications metrics: {error.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Hero Macro Metric Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-card/80 to-card/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
              Total Publications
            </span>
            <Layers className="h-4 w-4 text-primary" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono text-foreground">
              {data ? data.totalListings.toLocaleString() : "..."}
            </span>
            <span className="text-xs text-emerald-400 font-medium">
              {activeRate}% active
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">Across all tenant catalogs</p>
        </div>

        <div className="rounded-2xl border border-border bg-gradient-to-br from-card/80 to-card/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
              Active On Market
            </span>
            <TrendingUp className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono text-emerald-400">
              {data ? data.activeListings.toLocaleString() : "..."}
            </span>
            <span className="text-[11px] text-muted-foreground font-mono">
              / {data?.totalListings || 0}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">Live & syndicated units</p>
        </div>

        <div className="rounded-2xl border border-border bg-gradient-to-br from-card/80 to-card/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
              Geographic Coverage
            </span>
            <Globe2 className="h-4 w-4 text-blue-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono text-foreground">
              {data ? data.countriesCount : "..."}
            </span>
            <span className="text-xs text-muted-foreground">
              Countries • {data?.zonesCount || 0} Zones
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">Multi-jurisdiction distribution</p>
        </div>

        <div className="rounded-2xl border border-border bg-gradient-to-br from-card/80 to-card/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
              Syndication Reach
            </span>
            <Share2 className="h-4 w-4 text-purple-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono text-foreground">
              {data?.portals?.length || 4}
            </span>
            <span className="text-xs text-purple-400 font-medium">Portals Ready</span>
          </div>
          <p className="text-[11px] text-muted-foreground">PortalInmobiliario, Idealista, etc.</p>
        </div>
      </div>

      {/* Section 1: Visual Country Market Cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">
              National Market Breakdown
            </h2>
          </div>
          <span className="text-xs text-muted-foreground">Click country to filter regional zones below</span>
        </div>

        {isLoading ? (
          <div className="h-28 rounded-xl border border-border flex items-center justify-center text-xs text-muted-foreground font-mono">
            Aggregating national market metrics...
          </div>
        ) : !data?.byCountry || data.byCountry.length === 0 ? (
          <div className="h-24 rounded-xl border border-border flex items-center justify-center text-xs text-muted-foreground">
            No country publication data available.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.byCountry.map((c) => {
              const isSelected = selectedCountry === c.countryCode;
              const flag = COUNTRY_FLAGS[c.countryCode] || "🌐";
              const share = data.totalListings > 0
                ? Math.round((c.totalListings / data.totalListings) * 100)
                : 0;

              return (
                <button
                  key={c.countryCode}
                  onClick={() => setSelectedCountry(isSelected ? "ALL" : c.countryCode)}
                  className={`text-left rounded-2xl p-4 border transition-all relative overflow-hidden ${
                    isSelected
                      ? "border-primary bg-primary/10 shadow-lg shadow-primary/5 ring-1 ring-primary/40"
                      : "border-border bg-card/60 hover:bg-card hover:border-border/80"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="text-2xl" role="img" aria-label={c.countryName}>
                        {flag}
                      </span>
                      <div>
                        <div className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                          {c.countryName}
                          <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded border">
                            {c.countryCode}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {share}% of platform catalog
                        </p>
                      </div>
                    </div>

                    <Badge
                      variant="outline"
                      className={
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-emerald-500/30 text-emerald-400"
                      }
                    >
                      {c.activeListings} Active
                    </Badge>
                  </div>

                  {/* Progress Bar of Active vs Total */}
                  <div className="mt-4 space-y-1">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-muted-foreground text-[10px]">Market Active Rate</span>
                      <span className="font-semibold text-emerald-400">
                        {c.totalListings > 0 ? Math.round((c.activeListings / c.totalListings) * 100) : 0}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-muted/80 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{
                          width: `${c.totalListings > 0 ? (c.activeListings / c.totalListings) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 text-xs">
                    <div>
                      <span className="text-[10px] text-muted-foreground block">Catalog Views</span>
                      <span className="font-mono font-medium text-foreground">{c.totalViews} views</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-muted-foreground block">Inquiries</span>
                      <span className="font-mono font-medium text-foreground">{c.totalContacts} leads</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Section 2: Regional Zones & Syndication Ecosystem */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Zone & Metropolitan Concentration */}
        <Card className="lg:col-span-2 border-border">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Metropolitan Zones & Regions
                </CardTitle>
                <CardDescription className="text-xs">
                  Active publication volume grouped by metropolitan area.
                </CardDescription>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative w-44">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search zone or city..."
                    value={zoneSearch}
                    onChange={(e) => setZoneSearch(e.target.value)}
                    className="pl-8 h-8 text-xs bg-card"
                  />
                </div>
                {selectedCountry !== "ALL" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-primary"
                    onClick={() => setSelectedCountry("ALL")}
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredZones.length === 0 ? (
              <div className="h-32 flex flex-col items-center justify-center text-xs text-muted-foreground space-y-1">
                <MapPin className="h-6 w-6 text-muted-foreground/40" />
                <p>No regional zones match the current filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredZones.map((z, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-xl border border-border/80 bg-card/40 flex items-center justify-between hover:border-border transition-colors"
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-foreground">
                          {z.zone}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          ({z.countryCode})
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{z.city}</p>
                    </div>

                    <div className="text-right flex flex-col items-end gap-1">
                      <Badge
                        variant="outline"
                        className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-xs font-semibold"
                      >
                        {z.activeListings} Active
                      </Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {z.totalListings} total
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Syndication & Ecosystem Side Column */}
        <div className="space-y-6">
          {/* Inventory Lifecycle Status */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Filter className="h-4 w-4 text-primary" />
                Inventory Lifecycle
              </CardTitle>
              <CardDescription className="text-xs">
                Platform-wide status distribution.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {data?.byStatus?.map((st) => (
                <div
                  key={st.status}
                  className="flex items-center justify-between text-xs py-1.5 px-2.5 rounded-lg bg-card/40 border border-border/40"
                >
                  <span className="font-medium uppercase tracking-wider text-muted-foreground">
                    {st.status}
                  </span>
                  <span className="font-mono font-semibold text-foreground">
                    {st.count} units
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Syndication Channels */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Share2 className="h-4 w-4 text-primary" />
                Syndication Network
              </CardTitle>
              <CardDescription className="text-xs">
                Global property portal connectors.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { name: "Portal Inmobiliario", country: "CL", status: "ONLINE", units: 7 },
                { name: "Idealista", country: "ES", status: "CONNECTED", units: 0 },
                { name: "Fotocasa", country: "ES", status: "CONNECTED", units: 0 },
                { name: "Zillow Group", country: "US", status: "READY", units: 0 },
              ].map((portal, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 rounded-lg border border-border/50 bg-card/30 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    <span className="font-medium text-foreground">{portal.name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">({portal.country})</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {portal.units > 0 ? `${portal.units} live` : "Ready"}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 3: Cross-Tenant Recent Publication Activity */}
      <Card className="border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Cross-Tenant Publication Stream
              </CardTitle>
              <CardDescription className="text-xs">
                Recent inventory movements and status publications across all tenant brokerages.
              </CardDescription>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              Live Macro Stream
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {!data?.recentActivity || data.recentActivity.length === 0 ? (
            <div className="h-20 flex items-center justify-center text-xs text-muted-foreground">
              No recent publication events recorded.
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {data.recentActivity.map((item) => (
                <div
                  key={item.id}
                  className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-card/40 px-2 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                      {item.countryCode || "CL"}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-foreground flex items-center gap-2">
                        {item.title}
                        <Badge
                          variant="outline"
                          className={
                            item.status === "PUBLISHED" || item.status === "ACTIVE"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[10px]"
                              : "border-border text-muted-foreground text-[10px]"
                          }
                        >
                          {item.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                        <span className="font-medium text-foreground/80">{item.organizationName}</span>
                        <span>•</span>
                        <span>{item.city ? `${item.city}, ${item.countryCode || "CL"}` : "-"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right sm:shrink-0 flex sm:flex-col items-center sm:items-end justify-between">
                    <span className="font-mono text-xs font-semibold text-foreground">
                      {item.rentAmount ? `$${item.rentAmount.toLocaleString()} CLP` : "Rent not specified"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : "Just now"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default PublicationsPage;
